import React, { useReducer } from 'react';
import classNames from 'classnames';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import Paper from '@material-ui/core/Paper';
import AsyncSelect from 'react-select/async';
import { useOnMount, useSelectDebounce } from 'utils/react';
import { naturalSort } from 'utils/sorting';
import SearchIcon from '@material-ui/icons/Search';

import PageLoader from 'ui/PageLoader';
import PaperContainer from 'ui/PaperContainer';
import PageContainer from 'ui/PageContainer';
import ButtonsForm from 'ui/form/ButtonsForm';

import { withStyles, makeStyles } from '@material-ui/core';

const MODE_EDIT = 'edit';
const MODE_VIEW = 'view';

const initState = {
    loaded: false,
    loader: {
        main: false,
        lookups: false,
        loading: true,
    },
    lookups: {
        internal: {},
        external: {},
    },
    products: [],
    mode: MODE_VIEW,
};

const CHANGE = 'change';
const LOADED_PRODUCTS = 'loaded';
const LOADED_LOOKUPS = 'loaded.lookups';
const LOADING = 'loading';

const createAction = (action, payload) => ({ action, payload });

const changeAction = payload => createAction(CHANGE, payload);

const viewAction = () => createAction(MODE_VIEW);
const editAction = () => createAction(MODE_EDIT);

const loadedProductsAction = products =>
    createAction(LOADED_PRODUCTS, { products });
const loadedLookupsAction = lookups =>
    createAction(LOADED_LOOKUPS, { lookups });

const reducer = (state, { action, payload }) => {
    switch (action) {
        case LOADING:
            return {
                ...state,
                loader: {
                    ...state.loader,
                    loading: true,
                },
            };
        case LOADED_PRODUCTS:
            const { products } = payload;
            return {
                ...state,
                loader: {
                    ...state.loader,
                    loading: false,
                    main: true,
                },
                products,
            };
        case LOADED_LOOKUPS:
            const { lookups } = payload;
            return {
                ...state,
                loader: {
                    ...state.loader,
                    loading: false,
                    lookups: true,
                },
                lookups,
            };

        case CHANGE: {
            const { internal, external } = payload;
            const newProduct = {
                internal: {
                    id: internal.uuid,
                    name: internal.name,
                },
                external,
            };
            const products = state.products.concat();
            const index = products.findIndex(
                product => product.internal.id === internal.uuid
            );
            if (index === -1) {
                products.push(newProduct);
            } else if (external) {
                products.splice(index, 1, newProduct);
            } else {
                products.splice(index, 1);
            }
            return { ...state, products };
        }
        case MODE_VIEW:
        case MODE_EDIT:
            return { ...state, mode: action };
        default:
            throw Error(`Not recognize: ${action}`);
    }
};

export default function ProductMapping({
    service,
    config,
    localization,
    editable,
}) {
    const [state, dispatch] = useReducer(reducer, initState);
    const { loader, lookups, products, mode } = state;
    const { serviceProvider, label } = config;

    useOnMount(async () => {
        const response = await serviceProvider.products(service);
        const lookup = await loadInternalLookups();
        dispatch(loadedLookupsAction(lookup));
        dispatch(loadedProductsAction(response));
    });

    const loadInternalLookups = async () => {
        return serviceProvider.productsLookups(service, { lookup: 'internal' });
    };

    const loadExternalLookups = async search => {
        return serviceProvider.productsLookups(service, {
            search,
            lookup: 'external',
        });
    };

    const onEdit = async () => {
        if (editable) {
            dispatch(editAction());
        }
    };
    const onCancel = () => dispatch(viewAction());
    const onSave = () => {
        serviceProvider.updateProducts(service, products);
        dispatch(viewAction());
    };
    const onChange = (...args) => dispatch(changeAction(...args));

    const isEmpty = products.length === 0;
    const productsComponent = (
        <Products
            loading={loader.loading}
            config={config}
            mode={mode}
            lookups={lookups}
            products={products}
            onChange={onChange}
            localization={localization}
            loadExternalLookups={loadExternalLookups}
        />
    );

    let body = <PageLoader />;
    if (loader.main) {
        if (mode === MODE_VIEW) {
            if (loader.loading) {
                body = (
                    <Paper elevation={1}>
                        <PaperContainer.Body>
                            {productsComponent}
                        </PaperContainer.Body>
                    </Paper>
                );
            } else {
                body = (
                    <React.Fragment>
                        {loader.main && isEmpty && (
                            <ExpansionPanel onChange={onEdit}>
                                <ExpansionPanelSummary>
                                    {localization.productMappingEmpty}
                                </ExpansionPanelSummary>
                            </ExpansionPanel>
                        )}
                        {loader.main && !isEmpty && (
                            <Paper elevation={1}>
                                <PaperContainer.Body>
                                    {productsComponent}
                                    <ButtonsForm>
                                        {editable && (
                                            <ButtonsForm.BackButton
                                                label="Edit"
                                                onClick={onEdit}
                                            />
                                        )}
                                    </ButtonsForm>
                                </PaperContainer.Body>
                            </Paper>
                        )}
                    </React.Fragment>
                );
            }
        } else {
            body = (
                <Paper elevation={1}>
                    <PaperContainer.Body>
                        {productsComponent}
                        <ButtonsForm>
                            <ButtonsForm.BackButton onClick={onCancel} />
                            <ButtonsForm.NextButton
                                label="Save"
                                onClick={onSave}
                            />
                        </ButtonsForm>
                    </PaperContainer.Body>
                </Paper>
            );
        }
    }

    const usePageContainerStyles = makeStyles({
        root: { margin: '-34px auto' },
    });
    const pageContainerClasses = usePageContainerStyles();

    return (
        <PageContainer
            classes={{ root: pageContainerClasses.root }}
            title={localization.productMapping}
            subtitle={localization.productMappingSubtitle(label)}
        >
            {body}
        </PageContainer>
    );
}

function Products({
    mode,
    loading,
    onChange,
    products,
    config,
    localization,
    lookups,
    loadExternalLookups,
}) {
    const ProductComponent = mode === MODE_EDIT ? ProductsEdit : ProductsView;
    return (
        <div>
            <Row>
                <HeaderCell>{localization.productMappingPBColumn}</HeaderCell>
                <HeaderCell>
                    {localization.productMappingIntegrationColumn(config.label)}
                </HeaderCell>
            </Row>
            {loading && <PageLoader />}
            {!loading && (
                <ProductComponent
                    products={products}
                    lookups={lookups}
                    onChange={onChange}
                    loadExternalLookups={loadExternalLookups}
                />
            )}
        </div>
    );
}

function ProductsEdit({ products, onChange, loadExternalLookups, lookups }) {
    const findProduct = internal =>
        products.find(product => product.internal.id === internal.uuid);
    return lookups.map(lookup => {
        const product = findProduct(lookup);
        const value =
            product && product.external
                ? { label: product.external.name, value: product.external.id }
                : '';
        return (
            <ProductEdit
                internal={lookup}
                value={value}
                onChange={onChange}
                loadExternalLookups={loadExternalLookups}
            />
        );
    });
}

const sortOptions = options => naturalSort(options, option => option.name);

function ProductEdit({
    internal,
    value,
    options,
    onChange,
    loadExternalLookups,
}) {
    const onChangeEvent = item => {
        const id = item ? item.value : null;
        const external = id
            ? {
                  id,
                  name: item.label,
              }
            : null;
        onChange({
            internal,
            external,
        });
    };

    const loadOptions = value =>
        new Promise(async resolve => {
            if (!value) {
                resolve([]);
            }
            let products = await loadExternalLookups(value);
            products = sortOptions(products);
            resolve(
                products.map(product => ({
                    label: product.name,
                    value: product.id,
                }))
            );
        });

    const { setSearchTerm, setCallback } = useSelectDebounce(loadOptions);
    const handleLoadOptions = (term, callback) => {
        setCallback(() => {
            return callback;
        });
        setSearchTerm(term);
    };

    return (
        <EditRow>
            <Cell>{internal.name}</Cell>
            <Cell block>
                <AsyncSelect
                    value={value || ''}
                    onChange={onChangeEvent}
                    cacheOptions
                    loadOptions={loadOptions && handleLoadOptions}
                    isSearchable
                    isClearable
                    placeholder="Search..."
                    noOptionsMessage={() => 'Start typing to search'}
                    components={{
                        DropdownIndicator,
                    }}
                />
            </Cell>
        </EditRow>
    );
}

function ProductsView({ products }) {
    if (products.length) {
        const key = product => `${product.internal.id}.${product.external.id}`;
        return products.map(product => (
            <Row key={key(product)}>
                <TextCell>{product.internal.name}</TextCell>
                <TextCell>{product.external.name}</TextCell>
            </Row>
        ));
    }
    return null;
}

const styles = theme => ({
    row: {
        display: 'flex',
    },
    rowBorder: {
        border: `solid 1px ${theme.palette.text.secondary}`,
        borderRadius: theme.spacing() / 2,
        padding: `${theme.spacing() / 2}px ${theme.spacing()}px`,
        marginTop: theme.spacing(),
    },
    cell: {
        flexGrow: 1,
        width: '50%',
        display: 'flex',
        alignItems: 'center',
    },
    cellPadding: {
        paddingTop: theme.spacing(),
        paddingBottom: theme.spacing(),
    },
    cellBold: {
        fontWeight: 500,
    },
    cellText: {
        color: theme.palette.text.secondary,
    },
    cellBlock: {
        display: 'block',
    },
    searchIcon: {
        width: 36,
        color: 'hsl(0,0%,80%)',
        '&:hover': {
            color: 'hsl(0,0%,60%)',
        },
    },
    searchIconActive: {
        width: 36,
        color: 'hsl(0,0%,40%)',
    },
});

const Row = withStyles(styles)(({ border, children, classes }) => {
    const className = classNames(classes.row, {
        [classes.rowBorder]: border,
    });
    return <div className={className}>{children}</div>;
});

const Cell = withStyles(styles)(
    ({ bold, padding, text, children, classes, block }) => {
        const className = classNames(classes.cell, {
            [classes.cellBold]: bold,
            [classes.cellPadding]: padding,
            [classes.cellText]: text,
            [classes.cellBlock]: block,
        });
        return <div className={className}>{children}</div>;
    }
);

const HeaderCell = ({ children }) => (
    <Cell bold padding>
        {children}
    </Cell>
);

const EditRow = ({ children }) => <Row border>{children}</Row>;

const TextCell = ({ children }) => (
    <Cell text padding>
        {children}
    </Cell>
);

const DropdownIndicator = withStyles(styles)(({ classes, isFocused }) => {
    const className = classNames({
        [classes.searchIcon]: !isFocused,
        [classes.searchIconActive]: isFocused,
    });
    return <SearchIcon className={className} />;
});
